import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';

const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

// Convert base64 to Uint8Array for applicationServerKey
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function usePushNotifications() {
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [permission, setPermission] = useState('default');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if push notifications are supported, register SW, and auto-resubscribe on mount
  useEffect(() => {
    const init = async () => {
      const supported = 'serviceWorker' in navigator && 
                       'PushManager' in window && 
                       'Notification' in window;
      setIsSupported(supported);
      
      if (!supported) {
        setLoading(false);
        return;
      }
      
      setPermission(Notification.permission);
      if (localStorage.getItem('trainnova:push-disabled') === 'true') {
        setIsSubscribed(false);
        setLoading(false);
        return;
      }
      
      try {
        // Always register/update the service worker
        await navigator.serviceWorker.register('/sw.js', { scope: '/' });
        
        // Check if already subscribed in browser
        const registration = await navigator.serviceWorker.ready;
        let subscription = await registration.pushManager.getSubscription();
        
        // Self-heal: permission is granted but there is NO browser push
        // subscription (common on iOS PWA after a SW reinstall, or when the
        // subscription was never persisted to the backend). Create one now with
        // the canonical VAPID key so the backend gets a valid subscription
        // without any extra user interaction. This is the concrete fix for
        // "notifications enabled but no system push arrives": recipients ended
        // up with 0 rows in push_subscriptions.
        if (!subscription && Notification.permission === 'granted') {
          try {
            let vapidKey = VAPID_PUBLIC_KEY;
            if (!vapidKey) {
              const keyResponse = await api.get('/push/vapid-key');
              vapidKey = keyResponse.data.publicKey;
            }
            if (vapidKey) {
              subscription = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(vapidKey),
              });
              console.log('[Push] Auto-created missing subscription');
            }
          } catch (e) {
            console.debug('[Push] Auto-create subscription failed:', e.message);
          }
        }
        
        setIsSubscribed(!!subscription);
        
        // Auto-resubscribe: if browser has a valid subscription AND permission is granted,
        // silently re-send it to the backend. This handles SW updates, domain changes,
        // and new deploys where the backend may have lost the subscription record.
        if (subscription && Notification.permission === 'granted') {
          const token = localStorage.getItem('token');
          if (token) {
            try {
              const subJson = subscription.toJSON();
              await api.post('/push/subscribe', {
                endpoint: subJson.endpoint,
                keys: subJson.keys
              });
              console.log('[Push] Auto-resubscribed to backend');
            } catch (e) {
              console.debug('[Push] Auto-resubscribe failed (will retry on next load):', e.message);
            }
          }
        }
      } catch (err) {
        console.error('[Push] Init error:', err);
      }
      
      setLoading(false);
    };
    
    init();
  }, []);

  // Subscribe to push notifications
  const subscribe = useCallback(async () => {
    if (!isSupported) {
      setError('Push-notifikationer stods inte i din webblasare');
      return false;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Request permission first
      console.log('[Push] Requesting notification permission...');
      const result = await Notification.requestPermission();
      setPermission(result);
      console.log('[Push] Permission result:', result);
      
      if (result !== 'granted') {
        setError('Notifikationstillatelse nekad');
        setLoading(false);
        return false;
      }
      
      // Ensure service worker is registered and ready
      console.log('[Push] Registering service worker...');
      const swReg = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
      console.log('[Push] SW registered, waiting for ready...');
      const registration = await navigator.serviceWorker.ready;
      console.log('[Push] SW ready, active:', !!registration.active);
      
      // Get VAPID key
      let vapidKey = VAPID_PUBLIC_KEY;
      if (!vapidKey) {
        try {
          console.log('[Push] Fetching VAPID key from server...');
          const keyResponse = await api.get('/push/vapid-key');
          vapidKey = keyResponse.data.publicKey;
          console.log('[Push] VAPID key received:', vapidKey?.substring(0, 20) + '...');
        } catch (e) {
          console.error('[Push] VAPID key fetch failed:', e);
          setError('Kunde inte hamta VAPID-nyckel');
          setLoading(false);
          return false;
        }
      } else {
        console.log('[Push] Using env VAPID key:', vapidKey?.substring(0, 20) + '...');
      }
      
      // Subscribe to push manager
      console.log('[Push] Creating push subscription...');
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey)
      });
      console.log('[Push] Push subscription created:', subscription.endpoint?.substring(0, 60) + '...');
      
      // Send subscription to backend
      const subscriptionJson = subscription.toJSON();
      console.log('[Push] Sending subscription to backend...');
      const backendResponse = await api.post('/push/subscribe', {
        endpoint: subscriptionJson.endpoint,
        keys: subscriptionJson.keys
      });
      console.log('[Push] Backend response:', backendResponse.data);
      
      localStorage.removeItem('trainnova:push-disabled');
      setIsSubscribed(true);
      setLoading(false);
      return true;
    } catch (err) {
      console.error('[Push] Subscribe error:', err);
      console.error('[Push] Error name:', err.name);
      console.error('[Push] Error message:', err.message);
      if (err.response) {
        console.error('[Push] Backend error status:', err.response.status);
        console.error('[Push] Backend error data:', err.response.data);
      }
      setError(err.message || 'Prenumeration misslyckades');
      setLoading(false);
      return false;
    }
  }, [isSupported]);

  // Unsubscribe from push notifications
  const unsubscribe = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        // Remove this browser only, preserving the user's other devices.
        await api.post('/notifications/unsubscribe', { endpoint: subscription.endpoint });
        localStorage.setItem('trainnova:push-disabled', 'true');
        await subscription.unsubscribe();
      } else {
        localStorage.setItem('trainnova:push-disabled', 'true');
      }

      setIsSubscribed(false);
      setLoading(false);
      return true;
    } catch (err) {
      console.error('[Push] Unsubscribe error:', err);
      setError(err.message || 'Avprenumeration misslyckades');
      setLoading(false);
      return false;
    }
  }, []);

  // Send test notification
  const sendTestNotification = useCallback(async () => {
    const response = await api.post('/push/send-test');
    return response.data;
  }, []);

  return {
    isSupported,
    isSubscribed,
    permission,
    loading,
    error,
    subscribe,
    unsubscribe,
    sendTestNotification
  };
}

export default usePushNotifications;
